import React, { useEffect, useState } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import User from "../models/user";
import {
  applyColorMode,
  applySpooky,
  isColorModePreference,
  readCookiePreference,
  readHostMode,
  readHostSpooky,
  resolveColorMode,
} from "./colorMode";
import SpookyDecorations from "./SpookyDecorations";

// MUI needs literal colors; these mirror the dark tokens in theme/colors.css.
const DARK_PALETTE = {
  mode: "dark",
  background: { default: "#131519", paper: "#22262c" },
  text: { primary: "#e3e6eb", secondary: "#a1a8b5", disabled: "#6e7686" },
  divider: "#343841",
};

const darkTheme = createTheme({
  palette: DARK_PALETTE,
  components: {
    MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
    // colors.css fills bare dark-mode inputs with --surface-card; a MUI input already
    // sits in its field (often on a dialog paper), so it must stay see-through.
    // "&&" doubles the class to outrank html.dark :where(input) (0,1,1).
    MuiInputBase: { styleOverrides: { input: { "&&": { backgroundColor: "transparent" } } } },
  },
});
// Mirrors the html.dark.spooky tokens in theme/colors.css.
const spookyTheme = createTheme({
  palette: {
    mode: "dark",
    background: { default: "#170f23", paper: "#281f37" },
    text: { primary: "#e8e3f3", secondary: "#aaa5b4", disabled: "#564e66" },
    divider: "#3b324b",
  },
  components: darkTheme.components,
});
const lightTheme = createTheme();

/** Applies the user's app-wide light/dark choice to the sidebar. */
export const ColorModeProvider = ({ children }) => {
  const [preference, setPreference] = useState(() => readCookiePreference() || "light");
  const [, setSystemTick] = useState(0);
  // Set when the sidebar is embedded in the Addy app, which owns the choice.
  const [hostMode, setHostMode] = useState(readHostMode);
  const [isHostSpooky, setIsHostSpooky] = useState(readHostSpooky);

  useEffect(() => {
    const onMessage = (event) => {
      const { key, value } = event.data || {};
      if (key === "colorMode" && (value === "light" || value === "dark")) setHostMode(value);
      if (key === "spookyMode" && typeof value === "boolean") setIsHostSpooky(value);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  useEffect(() => {
    const email = new URLSearchParams(window.location.search).get("email");
    if (!email || readHostMode()) return;
    let cancelled = false;
    let lastCheck = 0;
    // The account is the source of truth; re-read it when the sidebar comes
    // back into view so a switch made in the Addy app on any device shows up.
    const loadAccountMode = () => {
      if (document.visibilityState !== "visible" || Date.now() - lastCheck < 30000) return;
      lastCheck = Date.now();
      User.getUserInfo(email).then((result) => {
        const accountMode = result?.response?.colorMode;
        if (!cancelled && isColorModePreference(accountMode)) setPreference(accountMode);
      }).catch(() => {});
    };
    loadAccountMode();
    document.addEventListener("visibilitychange", loadAccountMode);
    window.addEventListener("focus", loadAccountMode);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", loadAccountMode);
      window.removeEventListener("focus", loadAccountMode);
    };
  }, []);

  useEffect(() => {
    if (preference !== "system" || typeof window.matchMedia !== "function") return;
    const query = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => setSystemTick((tick) => tick + 1);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, [preference]);

  // Re-read on every render: an OS switch bumps systemTick, which re-renders.
  const mode = hostMode || resolveColorMode(preference);

  const isSpooky = mode === "dark" && isHostSpooky;

  useEffect(() => {
    applyColorMode(mode);
  }, [mode]);

  useEffect(() => {
    applySpooky(isSpooky);
  }, [isSpooky]);

  const theme = isSpooky ? spookyTheme : mode === "dark" ? darkTheme : lightTheme;

  return (
    <ThemeProvider theme={theme}>
      {children}
      {isSpooky && <SpookyDecorations />}
    </ThemeProvider>
  );
};
